import { Suspense, useEffect, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './stores/authStore';
import { PinLogin } from './components/PinLogin';
import { TabBar } from './components/TabBar';
import { Sidebar } from './components/Sidebar';
import { TopBar } from './components/TopBar';
import { FAB } from './components/FAB';
import { PermissionGate } from './components/PermissionGate';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import { modules } from './features/registry';
import { getPermLevel } from './lib/permissions';

// Lazy load main pages
const HoyPage = lazy(() => import('./features/hoy/HoyPage'));
const VaqueroShell = lazy(() => import('./features/vaquero/VaqueroShell'));
const AdminShell = lazy(() => import('./features/admin/AdminShell'));
const SesionesPage = lazy(() => import('./features/sesiones/SesionesPage'));
const SesionDetailPage = lazy(() => import('./features/sesiones/SesionDetailPage'));
const BuscarPage = lazy(() => import('./features/buscar/BuscarPage'));
const AnimalDetailPage = lazy(() => import('./features/buscar/AnimalDetailPage'));
const DatosPage = lazy(() => import('./features/datos/DatosPage'));
const PerfilPage = lazy(() => import('./features/perfil/PerfilPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function Loading() {
  return (
    <div className="flex items-center justify-center p-8">
      <div className="text-sm text-[var(--text-tertiary)]">Cargando...</div>
    </div>
  );
}

function AppLayout() {
  return (
    <div className="flex h-screen h-[100dvh] bg-[var(--bg-primary)]">
      {/* Desktop sidebar */}
      <Sidebar />

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Desktop top bar */}
        <TopBar />

        {/* Content area */}
        <main className="flex-1 overflow-y-auto bg-[var(--bg-secondary)] hide-scrollbar pb-[calc(64px+env(safe-area-inset-bottom,8px))] lg:pb-0">
          <ErrorBoundary>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<HoyPage />} />
              <Route path="/sesiones" element={<SesionesPage />} />
              <Route path="/sesiones/:id" element={<SesionDetailPage />} />
              {/* Redirect old /manejo to /sesiones */}
              <Route path="/manejo" element={<Navigate to="/sesiones" replace />} />
              {/* Module routes from registry (FAB quick actions) */}
              {modules.map((mod) => (
                <Route
                  key={mod.id}
                  path={`/manejo/${mod.route}`}
                  element={
                    <PermissionGate required={mod.requiredPermissions}>
                      <mod.component />
                    </PermissionGate>
                  }
                />
              ))}
              <Route path="/buscar" element={<BuscarPage />} />
              <Route path="/buscar/:eid" element={<AnimalDetailPage />} />
              <Route path="/datos" element={<DatosPage />} />
              <Route path="/perfil" element={<PerfilPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </main>

        {/* Mobile tab bar */}
        <TabBar />
      </div>

      {/* FAB */}
      <FAB />
    </div>
  );
}

function AuthGate() {
  const token = useAuthStore((s) => s.token);
  const role = useAuthStore((s) => s.role);
  const theme = useAuthStore((s) => s.theme);
  const logout = useAuthStore((s) => s.logout);

  // Apply theme on mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Sprint 3.5f — Bug 3: api.ts dispatches `manga-auth-lost` when a request
  // returns 401. Clear the zustand store so the next render falls back to
  // PinLogin and the vaquero gets a chance to type their PIN again instead
  // of seeing the meaningless "No autenticado" banner forever.
  useEffect(() => {
    const onLost = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      // eslint-disable-next-line no-console
      console.warn('[auth] session lost — forcing re-login', detail);
      logout();
      try {
        // Best-effort hint to the user. Don't block render on it.
        alert('La sesión se cerró. Volvé a entrar con tu PIN.');
      } catch {
        /* swallow */
      }
    };
    window.addEventListener('manga-auth-lost', onLost);
    return () => window.removeEventListener('manga-auth-lost', onLost);
  }, [logout]);

  if (!token) {
    return <PinLogin />;
  }

  // Vaquero (Operador Lechería) gets a stripped-down full-screen shell —
  // no sidebar, no top bar, no FAB, no tab bar.
  if (role === 'vaquero') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Loading />}>
          <VaqueroShell />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Sprint 3.5b — coordinator role (Alejandra + Glenda) lands on AdminShell.
  // Sprint 3.7c — owner-level users (Chris + Ileana) also land on AdminShell
  // so they see Finanzas / Reportes / Alertas by default instead of the field
  // HoyPage. Either can flip back to the vaquero view via 'Vista de campo' in
  // the sidebar (calls setRole('vaquero') which sends them through the branch
  // above on next render). Route on permission level so any future role that
  // maps to 'owner' inherits the admin shell automatically.
  if (role === 'coordinator' || getPermLevel(role) === 'owner') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Loading />}>
          <AdminShell />
        </Suspense>
      </ErrorBoundary>
    );
  }

  return <AppLayout />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <AuthGate />
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
